/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RequireSession } from './RequireSession';
import { CircleLink } from '@/circle/CircleLink';
import { ErrorScreen } from './ErrorScreen';
import { AppLayout } from './AppLayout';
import { Placeholder } from '@/routes/Placeholder';
import { BoardView } from '@/board/BoardView';
import {
  ArchivedView,
  CalendarView,
  ListView,
  LoginView,
  MyDayView,
  NewTaskModal,
  TaskModal,
} from './lazyViews';

/**
 * Routes, per docs/react-migration/plan.md §6.
 *
 * `currentView` used to be a module variable, so a view was not linkable, not
 * bookmarkable, and the back button did nothing. Two consequences worth keeping
 * in mind while the rest is built:
 *
 *  - **The task modal is a route** (/board/task/:taskId), not a state flag.
 *    People paste task links to each other today and they do not work.
 *  - **The active circle is NOT in the URL.** It is server state, persisted per
 *    person; a link carrying it would silently switch the circle of whoever
 *    opened it.
 *
 * Five of these elements are fetched on demand — see `lazyViews.ts` for which
 * and why. The views inside `AppLayout` are covered by the boundary around its
 * `<Outlet />`; the sign-in screen sits outside that layout and carries its own.
 */
export const router = createBrowserRouter([
  {
    path: '/login',
    errorElement: <ErrorScreen />,
    // No visible fallback. This route is a full-page paint on a cold load, and a
    // spinner that shows for one frame before the artwork arrives is worse than
    // the page simply appearing.
    element: (
      <Suspense fallback={null}>
        <LoginView />
      </Suspense>
    ),
  },
  {
    element: <RequireSession />,
    // **Every authenticated route is behind this one**, so anything that throws
    // while rendering the shell, a view, a card or a modal lands on the branded
    // screen rather than on the router's developer fallback. The sign-in route
    // carries its own, since it sits outside this subtree.
    errorElement: <ErrorScreen />,
    children: [
      /**
       * **A circle link is its own page, and it sits outside `AppLayout`.**
       *
       * The active circle stays out of the board's URL — see the note at the
       * top — and this route is what makes a shareable link possible without
       * reversing that: following it is a deliberate act, so switching is the
       * thing that was asked for.
       *
       * Outside the shell because half the time the person cannot open this
       * circle at all, and drawing the topbar around it would show them a
       * switcher naming somewhere else entirely, over a board that is not the
       * one in the link. A member is here for a few hundred milliseconds on the
       * way to `/board`, so there is nothing for a shell to frame either.
       */
      { path: 'c/:circleId', element: <CircleLink /> },
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to="/board" replace /> },
          {
            path: 'board',
            element: <BoardView />,
            children: [
              // Nested, so the board stays mounted behind the modal when it is
              // opened from a card rather than from a cold link.
              // Before the :taskId route, or "new" would be read as an id.
              { path: 'task/new', element: <NewTaskModal /> },
              { path: 'task/:taskId', element: <TaskModal /> },
            ],
          },
          { path: 'list', element: <ListView /> },
          { path: 'calendar', element: <CalendarView /> },
          { path: 'my-day', element: <MyDayView /> },
          { path: 'archived', element: <ArchivedView /> },
          { path: 'admin/circles', element: <Placeholder name="Manage circles" phase="P4" /> },
          { path: 'admin/people', element: <Placeholder name="Administrators" phase="P4" /> },
          // Anything else is a mistyped or retired link, not an error worth a screen.
          { path: '*', element: <Navigate to="/board" replace /> },
        ],
      },
    ],
  },
]);
